import { Store, TeamMember } from '../interfaces';
import { AvailableAppointmentTimeString } from '../utils/helpers';
import {
  AccountOwner,
  Appointment,
  AppointmentPermissions,
  AvailabilityByTeamTypeParams,
  AvailabilityParams,
  CancelAppointmentDetails,
  CreateAppointmentRequest,
  RescheduleAppointmentRequest,
  UpdateAppointmentRequest,
  V3AvailabilityParams,
} from './types';
import { InteractionType } from '@lendi/core-constants';
import qs from 'qs';
import { request } from './createRequest';
import { AxiosResponse } from 'axios';
import { DateTime } from 'luxon';
import { REALLOCATE_PARTIAL_FAILED_ERROR_CODE, RESCHEDULE_PARTIAL_FAILED_ERROR_CODE } from '../utils/enums/statusCodes';
import { AppointmentReplacementPartialFailedError } from './errors';
import { getRescheduleFrom } from '../utils/helpers/reschedule-appointment';
import { mapValues } from 'lodash';

export interface Availability {
  datetime: string;
  brokerIds: string[];
}

export type V3AvailabilityType = {
  [key in InteractionType]: {
    availability: Availability[];
    id?: string;
    name?: string;
    address?: string;
    storeFound?: boolean;
  };
};

const APIVersionPath = '/v2';
const APIVersionV3Path = '/v3';

// Response formats have been updated in v2 to support additional properties in availability responses
const returnAvailabilityResponseData = (response: AxiosResponse<any>) => {
  const data = response.data.data;
  const currentTime = DateTime.local();

  // Filter out any returned availabilities prior to current time.
  return data && data.length
    ? data.filter(
        (availabilitySlot: AvailableAppointmentTimeString) => DateTime.fromISO(availabilitySlot.datetime) > currentTime
      )
    : [];
};

// transform v3 response body
export const returnV3AvailabilityResponseData = (response: AxiosResponse<any>) => {
  const data = response.data.data;
  const currentTime = DateTime.local();

  // Filter out any returned availabilities prior to current time.
  return mapValues(data, (value) => {
    const availability = (value.availability || []).filter((availabilitySlot: AvailableAppointmentTimeString) => {
      const timeslotDatetimeInstance = DateTime.fromISO(availabilitySlot.datetime);
      const isInFuture = timeslotDatetimeInstance > currentTime;

      return isInFuture;
    });

    if (value?.storeFound) {
      return {
        availability: availability,
        address: value.address,
        id: value.id,
        name: value.name,
        storeFound: value.storeFound,
      };
    }
    return { availability };
  });
};

//This api is to get all brokers' available times for a customer(using customer token)
export const getAvailableTimes = (
  availabilityParams?: AvailabilityParams
): Promise<AvailableAppointmentTimeString[]> => {
  return request
    .get(`${APIVersionPath}/availability/me`, {
      params: availabilityParams,
    })
    .then((res) => returnAvailabilityResponseData(res));
};

//My self V3
export const getV3AvailableTimes = (availabilityParams: V3AvailabilityParams): Promise<Partial<V3AvailabilityType>> => {
  return request
    .get(`${APIVersionV3Path}/availability/me`, {
      params: availabilityParams,
    })
    .then((res) => returnV3AvailabilityResponseData(res));
};

export const createAppointment = (appointmentRequest: CreateAppointmentRequest): Promise<Appointment> => {
  return request.post(`${APIVersionPath}/appointments`, appointmentRequest).then((res) => res.data.data);
};

export const rescheduleAppointment = (
  rescheduleFromAppointmentID: string,
  appointmentRequest: RescheduleAppointmentRequest
): Promise<Appointment> => {
  return request
    .post(`${APIVersionPath}/appointments/${rescheduleFromAppointmentID}/reschedule`, appointmentRequest)
    .then((res) => {
      if (res.status === RESCHEDULE_PARTIAL_FAILED_ERROR_CODE) {
        throw new Error(RESCHEDULE_PARTIAL_FAILED_ERROR_CODE.toString());
      }
      return res.data.data;
    });
};

export const createOrRescheduleAppointment = (
  appointmentRequest: CreateAppointmentRequest | RescheduleAppointmentRequest
): Promise<Appointment> => {
  const rescheduleFromAppointmentID: string | null = getRescheduleFrom();

  return rescheduleFromAppointmentID
    ? rescheduleAppointment(rescheduleFromAppointmentID, appointmentRequest)
    : createAppointment(appointmentRequest);
};

export const cancelAppointment = (
  appointmentId: string,
  appointmentRequest: CancelAppointmentDetails
): Promise<Appointment> => {
  return request
    .post(`${APIVersionPath}/appointments/${appointmentId}/cancel`, appointmentRequest)
    .then((res) => res.data.data);
};

// TODO: Consolidate getAvailableTimes() and getAvailableTimesByType() into one function. See ticket https://creditandfinance.atlassian.net/browse/AC-879
export const getAvailableTimesByType = async (
  appointmentType: InteractionType,
  postcode?: string,
  suburb?: string,
  storeId?: string
) => {
  return getAvailableTimes({
    type: appointmentType,
    ...(postcode && { postcode: postcode }),
    ...(suburb && { suburb: suburb }),
    ...(storeId && { storeId: storeId }),
  });
};

/**
 * getAllAvailableTimesByTeamType()
 *
 * Retrieves all team's available times for a customer(by query in url) with a chosen team member type.
 */
export const getAllAvailableTimesByTeamType = async (
  availabilityByTeamTypeParams?: AvailabilityByTeamTypeParams,
  LGSFIsActive: boolean = false
) => {
  return request
    .get(`${APIVersionPath}/availability`, {
      params: {
        customerId: availabilityByTeamTypeParams?.customerId,
        teamMemberTypeLGSF: availabilityByTeamTypeParams?.teamMemberTypeLGSF,
        type: availabilityByTeamTypeParams?.appointmentType,
        [LGSFIsActive ? 'teamMemberSkillsLGSF' : 'teamMemberSkills']: availabilityByTeamTypeParams?.teamMemberSkills,
        ...(LGSFIsActive && { teamMemberSubskills: availabilityByTeamTypeParams?.teamMemberSubskills }),
        ...(availabilityByTeamTypeParams?.postcode && { postcode: availabilityByTeamTypeParams?.postcode }),
        ...(availabilityByTeamTypeParams?.suburb && { suburb: availabilityByTeamTypeParams?.suburb }),
        ...(availabilityByTeamTypeParams?.storeId && { storeId: availabilityByTeamTypeParams?.storeId }),
      },
      paramsSerializer: (params) => {
        const finalParams = qs.stringify(
          {
            ...params,
            [LGSFIsActive ? 'teamMemberSkillsLGSF' : 'teamMemberSkills']: [
              ...availabilityByTeamTypeParams?.teamMemberSkills!,
            ],
            ...(LGSFIsActive && { teamMemberSubskills: [...availabilityByTeamTypeParams?.teamMemberSubskills!] }),
          },
          { encode: false, indices: false }
        );
        return finalParams;
      },
    })
    .then((res) => returnAvailabilityResponseData(res));
};

//Team member V3
export const getV3AllAvailableTimesByTeamType = async (
  availabilityByTeamTypeParams?: AvailabilityByTeamTypeParams,
  LGSFIsActive: boolean = false
): Promise<Partial<V3AvailabilityType>> => {
  return request
    .get(`${APIVersionV3Path}/availability`, {
      params: {
        customerId: availabilityByTeamTypeParams?.customerId,
        teamMemberTypeLGSF: availabilityByTeamTypeParams?.teamMemberTypeLGSF,
        postcode: availabilityByTeamTypeParams?.postcode,
        suburb: availabilityByTeamTypeParams?.suburb,
        storeId: availabilityByTeamTypeParams?.storeId,
        types: availabilityByTeamTypeParams?.types,
        [LGSFIsActive ? 'teamMemberSkillsLGSF' : 'teamMemberSkills']: availabilityByTeamTypeParams?.teamMemberSkills,
        ...(LGSFIsActive && { teamMemberSubskills: availabilityByTeamTypeParams?.teamMemberSubskills }),
      },
      paramsSerializer: (params) => {
        const finalParams = qs.stringify(
          {
            ...params,
            [LGSFIsActive ? 'teamMemberSkillsLGSF' : 'teamMemberSkills']: [
              ...availabilityByTeamTypeParams?.teamMemberSkills!,
            ],
            ...(LGSFIsActive && { teamMemberSubskills: [...availabilityByTeamTypeParams?.teamMemberSubskills!] }),
          },
          { encode: false, indices: false }
        );
        return finalParams;
      },
    })
    .then((res) => returnV3AvailabilityResponseData(res));
};

export const updateAppointmentNotes = (
  appointmentId: string,
  requestBody: UpdateAppointmentRequest
): Promise<Appointment> => {
  return request.put(`${APIVersionPath}/appointments/${appointmentId}`, requestBody).then((res) => res.data);
};

export const updateAppointmentOwner = (appointmentId: string, teamMemberId: string): Promise<Appointment> => {
  return request
    .put(`${APIVersionPath}/appointments/${appointmentId}/appointment-owner`, { brokerId: teamMemberId })
    .then((res) => {
      if (res.status === REALLOCATE_PARTIAL_FAILED_ERROR_CODE) {
        throw new AppointmentReplacementPartialFailedError(
          `Updating appointment owner partially failed for appointment: ${appointmentId}. The original appointment ${appointmentId} has been cancelled, but a new appointment for team member with id ${teamMemberId} failed to be created.`
        );
      }

      return res.data.data;
    });
};

// this endpoint is to get all brokers belong to 3rd party profiles: Broker, Broker Manager and Senior Relationship Manager
export const getTeamMembersForSpecificRoles = async (includeBuyersAgent = false, includeStoreData = false) => {
  const queryParams = includeStoreData ? '?include=store' : '';
  return request.get(`${APIVersionPath}/appointments/team-members${queryParams}`).then((res) => {
    const brokers = res.data?.brokers || res.data;
    const buyersAgents = res.data?.buyersAgents;
    return includeBuyersAgent ? { brokers, buyersAgents } : brokers;
  });
};

export const getAccountOwnerByCustomer = (customerId: string | undefined): Promise<AccountOwner> => {
  return request.get(`${APIVersionPath}/appointments/account-owners/${customerId}`).then((res) => res.data);
};

export const getAvailableTimesByBrokerId = (
  brokerId: string,
  appointmentType: InteractionType
): Promise<AvailableAppointmentTimeString[]> => {
  return request
    .get(`${APIVersionPath}/availability/${brokerId}`, {
      params: {
        type: appointmentType,
      },
    })
    .then((res) => returnAvailabilityResponseData(res));
};

//Specific someone V3
export const getV3AvailableTimesByBrokerId = (
  brokerId: string,
  availabilityParams?: V3AvailabilityParams
): Promise<Partial<V3AvailabilityType>> => {
  return request
    .get(`${APIVersionV3Path}/availability/${brokerId}`, {
      params: availabilityParams,
    })
    .then((res) => returnV3AvailabilityResponseData(res));
};

export const getAccountOwners = (customerId: string): Promise<AccountOwner> => {
  return request.get(`${APIVersionPath}/appointments/account-owners/${customerId}`).then((res) => res.data);
};

export const getAllAppointmentsByCustomerId = (customerId: string | undefined): Promise<Appointment[]> => {
  return request.get(`${APIVersionPath}/appointments?customerId=${customerId}`).then((res) => res.data.data);
};

export const getAppointmentPermissionsByCustomerId = (
  customerId: string | undefined
): Promise<AppointmentPermissions> => {
  return request
    .get(`${APIVersionPath}/appointments/permissions?customerId=${customerId}`)
    .then((res) => res.data.data);
};

export const getTeamMemberById = (teamMemberId: string): Promise<TeamMember> => {
  return request.get(`${APIVersionPath}/appointments/team-members/${teamMemberId}`).then((res) => res.data);
};

export const getStoreInfoByStoreId = (storeId: string): Promise<Store> => {
  return request.get(`${APIVersionPath}/appointments/stores/${storeId}`).then((res) => res.data);
};
